import { _ } from "libs";

import React, { useState, FC, useEffect } from "react";
import withRedux from "../withRedux";
import ClubHeader from "../components/club/clubHeader";
import ClubSidebar from "../components/club/clubSidebar";
import ClubAbout from "../components/club/clubAbout";
import BookingWizard from "../components/club/bookingWizard/bookingWizard";
import { ReservationState } from "../reducers/reservation";
import { Club, Court, Weather, SlotsData, Connected, Slot } from "../types";
import {
	loadClub,
	loadClubWeather,
	loadClubSlots,
	getReservationContext,
	clearReservationContext,
	favoriteClub,
	unfavoriteClub,
	showUnavailability,
	requestClubMembership,
	cancelClubMembershipRequest,
} from "../actions";
import Loader from "../components/loader";
import moment from "moment";
import queryString from "query-string";
import { RouteChildrenProps } from "react-router";
import { RootState } from "../reducers";
import { AuthState } from "../reducers/auth";
import Timeline from "../components/club/timeline";
import { Location } from "history";

type Props = RouteChildrenProps<any> & {
	preloadedClub?: Club;
	auth: AuthState;
	match: {
		params: {
			uuid: string;
		};
	};
	reservation: ReservationState;
	widgetMode?: boolean;
	loadClub: Connected<typeof loadClub>;
	loadClubWeather: Connected<typeof loadClubWeather>;
	getReservationContext: Connected<typeof getReservationContext>;
	clearReservationContext: Connected<typeof clearReservationContext>;
	showUnavailability: Connected<typeof showUnavailability>;
	requestClubMembership: Connected<typeof requestClubMembership>;
	cancelClubMembership: Connected<typeof cancelClubMembershipRequest>;
	favoriteClub: Connected<typeof favoriteClub>;
	unfavoriteClub: Connected<typeof unfavoriteClub>;
	onLoginButtonClicked?: () => void;
	onLogoutButtonClicked?: () => void;
};

type State = {
	timestamp: number;
};

function getTimestamp(location: Location): number {
	let timestamp = null as number;

	if (location.state) {
		timestamp = (location.state as State).timestamp;
	} else {
		const parsed = queryString.parseUrl(location.search);
		timestamp = parsed.query.timestamp !== undefined ? parseInt(parsed.query.timestamp as string) : moment().unix();
	}

	return timestamp;
}

const ClubPage: FC<Props> = ({
	preloadedClub = null,
	auth,
	location,
	loadClub,
	loadClubWeather,
	getReservationContext,
	clearReservationContext,
	reservation,
	favoriteClub,
	unfavoriteClub,
	showUnavailability,
	requestClubMembership,
	cancelClubMembership,
	widgetMode = false,
	onLoginButtonClicked,
	onLogoutButtonClicked,
	...props
}: Props) => {
	const {
		match: { params },
	} = props;

	const { uuid } = params;

	const [state, setState] = useState({
		club: preloadedClub,
		slots: null as SlotsData,
		weather: null as Weather,
		loading: preloadedClub ? false : true,
		timestamp: getTimestamp(location),
	});

	const loadAll = async (uuid: string, timestamp: number) => {
		const promises = [loadClub(uuid), loadClubWeather(uuid, timestamp), loadClubSlots(uuid, timestamp)];
		const [clubResult, weatherResult, slotsResult] = await Promise.allSettled(promises);

		const club = (clubResult.status === "fulfilled" ? clubResult.value : null) as Club;
		const weather = (weatherResult.status === "fulfilled" ? weatherResult.value : null) as Weather;
		const slots = (slotsResult.status === "fulfilled" ? slotsResult.value : null) as SlotsData;

		setState({ club, weather, slots, timestamp, loading: false });
	};

	const loadWeatherAndSlots = async (uuid: string, timestamp: number) => {
		const promises = [loadClubWeather(uuid, timestamp), loadClubSlots(uuid, timestamp)];
		const [weatherResult, slotsResult] = await Promise.allSettled(promises);

		const weather = (weatherResult.status === "fulfilled" ? weatherResult.value : null) as Weather;
		const slots = (slotsResult.status === "fulfilled" ? slotsResult.value : null) as SlotsData;

		setState({ ...state, weather, slots, timestamp });
	};

	const loadSlots = async (uuid: string, timestamp: number) => {
		const slots = await loadClubSlots(uuid, timestamp);
		setState({ ...state, slots, timestamp });
	};

	const onBookingSuccess = () => {
		loadSlots(uuid, timestamp);
	};

	useEffect(() => {
		if (!preloadedClub) {
			loadAll(uuid, timestamp);
		} else {
			loadWeatherAndSlots(uuid, timestamp);
		}
	}, [uuid]);

	const onTimeChange = (timestamp: number) => {
		if (state.club) {
			loadWeatherAndSlots(uuid, timestamp);
		}
	};

	useEffect(() => {
		if (state.slots) {
			const { slots } = state;
			const parsed = queryString.parseUrl(location.search);

			if (parsed.query["court"]) {
				const courtId = parsed.query["court"];

				let selectedSlot = null as Slot;
				let selectedCourt = null as Court;
				loop: for (let court of slots.courts) {
					if (court.courtId === courtId) {
						selectedCourt = court;
						for (let slot of court.slots) {
							if (slot.startDateTime === state.timestamp) {
								selectedSlot = slot;
								break loop;
							}
						}
					}
				}

				if (selectedCourt && selectedSlot) {
					if (selectedSlot.slotContext.available) {
						let params = {
							court: selectedCourt,
							slot: selectedSlot,
							...{
								duration: selectedSlot.slotContext.available.durations[0],
								startDateTime: state.timestamp,
							},
						};

						getReservationContext({ ...params, preloadOnly: true }).then(
							getReservationContext.bind(this, params)
						);
					} else {
						showUnavailability({ court: selectedCourt, slot: selectedSlot });
					}
				}
			}
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}, [state.slots, state.timestamp]);

	const { club, weather, slots, loading, timestamp } = state;
	const courts = slots ? slots.courts : [];

	if (loading) {
		return <Loader />;
	}

	if (!club) {
		return <div className="noResults">{_("clubs.notFound")}</div>;
	}

	const clubPrivateButNotMember =
		club.clubDetails.type === "private" &&
		(club.clubDetails.memberStatus === "not_member" ||
			club.clubDetails.memberStatus === "request_pending" ||
			club.clubDetails.memberStatus === "request_declined");

	const hasAvailableSlots = courts && Array.isArray(courts) && courts.length > 0;

	return (
		<div
			className="clubPage"
			data-standalone={widgetMode ? "true" : "false"}
			data-private-club-non-member={clubPrivateButNotMember}
			data-contracted-club={club.clubInfo.contractedClub}
		>
			<ClubHeader
				{...{ club, weather, favoriteClub, unfavoriteClub, timestamp }}
				onTimeChange={onTimeChange}
				widgetMode={widgetMode}
			/>

			{clubPrivateButNotMember === false ? (
				<div className="clubCalendar">
					{!hasAvailableSlots ? (
						<NoAvailableCourts />
					) : (
						<Timeline
							{...{
								club,
								slots,
								timestamp,
								getReservationContext,
								clearReservationContext,
								reservation,
								showUnavailability,
								widgetMode,
							}}
						/>
					)}
				</div>
			) : null}

			<ClubSidebar
				open={
					reservation.context !== null ||
					reservation.params.courtId !== null ||
					reservation.unavailableSlot !== null
				}
			>
				{clubPrivateButNotMember ? null : (
					<BookingWizard
						{...{
							reservation,
							club,
							courts,
							auth,
							onLoginButtonClicked,
							onBookingSuccess,
							widgetMode,
						}}
					/>
				)}
			</ClubSidebar>
			{!widgetMode ? (
				<ClubAbout
					club={club}
					onRequestMembershipClicked={requestClubMembership}
					onCancelClubMembershipClicked={cancelClubMembership}
				/>
			) : null}
		</div>
	);
};

const NoAvailableCourts = () => {
	return (
		<div className="noResults noAvailableCourts">
			<h5>{_("courts.noResults.heading")}</h5>
			<p />
		</div>
	);
};

const mapStateToProps = ({ auth, reservation, club, social: { friends } }: RootState, ownProps) => {
	return { auth, reservation, friends, club };
};

export default withRedux(
	{
		loadClub,
		loadClubWeather,
		getReservationContext,
		clearReservationContext,
		favoriteClub,
		unfavoriteClub,
		showUnavailability,
		requestClubMembership,
		cancelClubMembership: cancelClubMembershipRequest,
	},
	mapStateToProps
)(ClubPage);
